import React, { useContext, useEffect } from 'react'
import { Formik, Form, Field } from 'formik'
import { StaticTextareaField, StaticField, TextField } from './Form'
import { OptionsType, OptionTypeBase } from 'react-select'
import { CallTmStatusManagerContext } from '~/service/CallTmStatusManager'
import ButtonSelect from './Form/ButtonSelect'
import trans from '~/translations/trans'
import Rating, { StaticRatingField } from './Form/Rating'
import { commonInitial } from './common'
import { DescribeTelemarketingValues, FormProps, StaticProps } from './types'
import FieldNote from './Form/FieldNote'
import Submit from './Submit'
import { CallOnShow, TelemarketingContactOnShow } from '../../types'
import { useDidUpdate } from '~/utils/hooks'
import { formatDate, parseIsoDate } from '~/utils/dateFns'

const telemarketingInitial = {
  ...commonInitial,
  statuses: [],
  customStatusId: '',
  customStatusNote: '',
  // statusNote: '',
  failReason: '',
}

const failReasonOptions: () => [number, string, string][] = () => [
  [1, intl.formatMessage({ id: 'call / describe / call later' }), 'success'],
  [6, intl.formatMessage({ id: 'call / describe / aborted' }), 'success'],
  [2, intl.formatMessage({ id: 'call / describe / voicemail' }), 'success'],
  [3, intl.formatMessage({ id: 'call / describe / dont call' }), 'danger'],
  [4, intl.formatMessage({ id: 'call / describe / consent' }), 'danger'],
  [5, intl.formatMessage({ id: 'call / describe / wrong number' }), 'danger'],
]

export const DescribeTelemarketingStatic: React.FC<StaticProps> = ({
  call,
}) => {
  const { statuses } = useContext(CallTmStatusManagerContext)

  const failReason = failReasonOptions().find(([id]) => id == call.tmFailReason)

  return (
    <>
      <StaticField
        label={intl.formatMessage({
          id: 'call / describe / is success',
        })}
        value={call.tmSuccess ? trans.form.yes() : trans.form.no()}
        horizontal={false}
      />

      {call.tmSuccess ? (
        <>
          {call.tmStatuses.length > 0 && (
            <>
              <StaticField
                label={intl.formatMessage({
                  id: 'call / describe / outbound / status / status',
                })}
                value={call.tmStatuses
                  .map(id => statuses.find(status => status.id == id)?.name)
                  .join(', ')}
                horizontal={false}
              />

              {/* <StaticField
                label={intl.formatMessage({
                  id: 'call / describe / outbound / status / status description',
                })}
                value={call.tmStatusNote ? call.tmStatusNote : '-'}
                horizontal={false}
              /> */}
            </>
          )}

          {call.tmCustomStatusId && (
            <DescribeTelemarketingStaticCustomStatus call={call} />
          )}
        </>
      ) : (
        <>
          <StaticField
            label={intl.formatMessage({
              id: 'call / describe / outbound / status / fail reason',
            })}
            value={failReason && failReason[1]}
            horizontal={false}
          />
        </>
      )}
      <StaticTextareaField
        label={intl.formatMessage({ id: 'call / describe / note' })}
        value={call.note ? call.note : '-'}
        horizontal={false}
      />
      <StaticRatingField value={call.rating} />
    </>
  )
}

type DescribeTelemarketingStaticCustomStatusProps = {
  call: CallOnShow
}

const DescribeTelemarketingStaticCustomStatus: React.FC<
  DescribeTelemarketingStaticCustomStatusProps
> = ({ call }) => {
  const id = call.tmCustomStatusId!
  const statuses = call.tmContact!.customStatuses!
  const status = statuses[id]
  const statusNote = call.tmCustomStatusNote!

  return (
    <>
      <StaticField
        label={intl.formatMessage({
          id: 'call / describe / outbound / status / status',
        })}
        value={customStatusValueToLabel(call.tmContact!, id)}
        horizontal={false}
      />

      {Array.isArray(status) && (
        <StaticField
          label={intl.formatMessage({
            id: 'call / describe / outbound / status / custom status note',
          })}
          value={
            status[1] == 'date'
              ? formatDate(new Date(statusNote), 'PPPP')
              : status[1] == 'datetime'
              ? formatDate(new Date(statusNote), 'PPPp')
              : statusNote
          }
          horizontal={false}
        />
      )}
    </>
  )
}

//

const DescribeTelemarketing: React.FC<FormProps> = ({
  call,
  formikConfig,
  showSubmit,
  submitRef,
}) => {
  const tmContact = call.tmContact!
  const { statuses: statusesAll } = useContext(CallTmStatusManagerContext)

  const statuses = statusesAll
    .filter(status => !status.removed)
    .filter(status => status.tmCampaignId == tmContact.tmCampaignId)

  const statusesOptions: OptionsType<OptionTypeBase> = statuses.map(subject => [
    subject.id,
    subject.name,
  ])

  const customStatusesOptions =
    tmContact.customStatuses && Object.keys(tmContact.customStatuses).length
      ? Object.keys(tmContact.customStatuses).map(key => [
          key,
          customStatusValueToLabel(tmContact, key),
        ])
      : null

  const initialValues = {
    ...telemarketingInitial,
    ...formikConfig.initialValues,
    type: 'tm',
  }

  return (
    <Formik<DescribeTelemarketingValues>
      {...formikConfig}
      validate={values => {
        formikConfig.validate && formikConfig.validate(values)

        const errors: any = {}

        if (values.success) {
          if (statuses.length && !values.statuses.length) {
            errors.statuses = intl.formatMessage({
              id: 'call / describe / outbound / status / validation / min one',
            })
          }

          if (customStatusesOptions) {
            if (!values.customStatusId) {
              errors.customStatusId = intl.formatMessage({
                id: 'validation / required',
              })
            } else if (
              Array.isArray(tmContact.customStatuses![values.customStatusId]) &&
              !values.customStatusNote
            ) {
              errors.customStatusNote = intl.formatMessage({
                id: 'validation / required',
              })
            }
          }
        } else {
          if (!values.failReason) {
            errors.failReason = intl.formatMessage({
              id: 'call / describe / outbound / status / validation / reason',
            })
          }
        }

        return errors
      }}
      initialValues={initialValues}
    >
      {({ isSubmitting, values, setFieldValue, submitForm }) => (
        <Form>
          <>
            {(() => {
              if (submitRef) {
                submitRef.current = submitForm
              }
            })()}

            <Field
              label={intl.formatMessage({
                id: 'call / describe / is success',
              })}
              name="success"
              horizontal={false}
              component={ButtonSelect}
              options={[
                [true, trans.form.yes()],
                [false, trans.form.no()],
              ]}
              afterChange={(val: boolean) => {
                if (val) {
                  setFieldValue('statuses', [])
                } else {
                  setFieldValue('failReason', '')
                }
              }}
            />

            {values.success ? (
              <>
                {statuses.length > 0 && (
                  <>
                    <Field
                      label={intl.formatMessage({
                        id: 'call / describe / outbound / status / status',
                      })}
                      name="statuses"
                      horizontal={false}
                      component={ButtonSelect}
                      options={statusesOptions}
                      multiple
                    />

                    {/* <Field
                    label={intl.formatMessage({
                      id: 'call / describe / outbound / status / status description',
                    })}
                    name="statusNote"
                    horizontal={false}
                    component={TextareaAutosizeField}
                    minRows={1}
                  /> */}
                  </>
                )}

                {customStatusesOptions && (
                  <DescribeTelemarketingCustomStatus
                    customStatusesOptions={customStatusesOptions}
                    setFieldValue={setFieldValue}
                    tmContact={tmContact}
                    values={values}
                  />
                )}
              </>
            ) : (
              <>
                <Field
                  label={intl.formatMessage({
                    id: 'call / describe / outbound / status / fail reason',
                  })}
                  name="failReason"
                  horizontal={false}
                  component={ButtonSelect}
                  options={failReasonOptions()}
                />
              </>
            )}

            <FieldNote />

            <Field name="rating" component={Rating} />

            {showSubmit && <Submit isSubmitting={isSubmitting} />}
          </>
        </Form>
      )}
    </Formik>
  )
}

export default DescribeTelemarketing

type DescribeTelemarketingCustomStatusProps = {
  customStatusesOptions: string[][]
  values: DescribeTelemarketingValues
  setFieldValue: (
    field: string,
    value: any,
    shouldValidate?: boolean | undefined
  ) => void
  tmContact: TelemarketingContactOnShow
}

const DescribeTelemarketingCustomStatus: React.FC<
  DescribeTelemarketingCustomStatusProps
> = ({ customStatusesOptions, values, setFieldValue, tmContact }) => {
  useDidUpdate(() => {
    setFieldValue('customStatusNote', '')
  }, [values.customStatusId])

  return (
    <>
      <Field
        label={intl.formatMessage({
          id: 'call / describe / outbound / status / status',
        })}
        name="customStatusId"
        horizontal={false}
        component={ButtonSelect}
        options={customStatusesOptions}
      />

      {(() => {
        if (values.customStatusId) {
          const status = tmContact.customStatuses![values.customStatusId]

          if (Array.isArray(status)) {
            return (
              <Field
                label={intl.formatMessage({
                  id: 'call / describe / outbound / status / custom status note',
                })}
                name="customStatusNote"
                horizontal={false}
                // component={status[1] ButtonSelect}
                type={status[1] == 'datetime' ? 'datetime-local' : status[1]}
                component={TextField}
              />
            )
          }
        }
      })()}
    </>
  )
}

//

const customStatusValueToLabel = (
  tmContact: TelemarketingContactOnShow,
  key: string
) => {
  const val = tmContact.customStatuses![key]
  const label = Array.isArray(val) ? val[0] : val
  const date = !label && parseIsoDate(key)

  return date ? formatDate(date, key.includes(':') ? 'PPPPp' : 'PPPP') : label
}
